import * as React from "react";
import { useRef, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { ClockIcon } from "@phosphor-icons/react/dist/ssr/Clock";
import dayjs from "dayjs";

interface Props {
	label: string;
  required?: boolean;
	value: string | null;
	setValue: (value: string | null | number) => void;
	placeholder?: string;
	fieldStyles?: any;
	error?: boolean;
	centered?: boolean;
}

export function CustomTimePicker({
	label,
	value,
  required,
	setValue,
	placeholder = "Select a time",
	fieldStyles = {},
	error = false,
	centered = false,
}: Props) {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const handleOpen = () => setIsOpen(true);
	const handleClose = () => setIsOpen(false);

	const handleTimeSelect = (time: string | null | number) => {
		setValue(time);
	};

	const valueTime = value ? dayjs(+value) : null;

	return (
		<Box sx={{ ...fieldStyles }}>
			<Box
				ref={anchorRef}
				component="button"
				type="button"
				onClick={handleOpen}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						handleOpen();
					}
				}}
				tabIndex={0}
				aria-label={label}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				sx={{
					...fieldStyles,
					display: "flex",
					alignItems: "center",
					gap: 1,
					width: "100%",
					backgroundColor: "transparent",
					p: 0,
					mt: 0,
					border: "none",
					outline: "none",
					boxShadow: "none",
					cursor: "pointer",
					// Keyboard focus ring and input border highlight
					"&:focus-visible .MuiInputBase-root": {
						borderColor: error ? "var(--mui-palette-error-main)" : "var(--mui-palette-primary-main)",
						boxShadow: error ? "0 0 0 1px var(--mui-palette-error-main)" : "0 0 0 1px var(--mui-palette-primary-main)",
					},
					"&:focus-visible .MuiInputLabel-root": {
						color: error ? "error.main" : "primary.main",
					},
          '& .MuiInputLabel-root': {
            alignSelf: 'flex-start !important',
          },
          // Keep focus styles when TimePicker is open
          "&[aria-expanded='true'] .MuiInputBase-root": {
            borderColor: error ? "var(--mui-palette-error-main)" : "var(--mui-palette-primary-main)",
            boxShadow: error ? "0 0 0 1px var(--mui-palette-error-main)" : "0 0 0 1px var(--mui-palette-primary-main)",
          },
          "&[aria-expanded='true'] .MuiInputLabel-root": {
            color: error ? "error.main" : "primary.main",
          },
				}}
			>
				<TextField
					variant="outlined"
					label={label}
          required={required}
          error={error}
					placeholder={placeholder}
					value={valueTime ? dayjs(valueTime).format("DD/MM/YYYY HH:mm") : ""}
					InputProps={{
						readOnly: true,
						error: error,
						endAdornment: (
							<Typography
								variant="subtitle2"
								color="text.secondary"
								style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1 }}
							>
								<ClockIcon  style={{ cursor: "pointer" }} width={24} height={24} weight="fill" />
							</Typography>
						),
					}}
					inputProps={{
						tabIndex: -1,
					}}
					sx={{
						flex: "1",
						justifyContent: "flex-start",
						textTransform: "none",
						cursor: "pointer",
						"& .MuiInputBase-input": {
							cursor: "pointer",
							fontSize: "16px",
						},
            // Label focus styles
            "& .MuiInputLabel-root": {
              "&.Mui-focused": {
                color: error ? "error.main" : "primary.main",
              },
            },
            // Input focus styles
            "& .MuiOutlinedInput-root": {
              '&:before': {
                content: 'none',
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: error ? "var(--mui-palette-error-main)" : "var(--mui-palette-primary-main)",
              },
            },

						...fieldStyles,
					}}
					focused={isOpen}
				/>
			</Box>

			<DateTimePicker
				open={isOpen}
				onClose={handleClose}
				value={valueTime}
        // @ts-expect-error time is not typed
        onChange={(time) => handleTimeSelect(time ? new Date(time).getTime() : null)}
				slotProps={{
					textField: { sx: { display: "none" } },
					popper: centered
						? {
								anchorEl: anchorRef.current,
								placement: "bottom",
								modifiers: [
									{
										name: "applyStyles",
										enabled: true,
										fn: ({ state }) => {
											if (state.elements.popper) {
												state.elements.popper.style.position = "fixed";
												state.elements.popper.style.top = "50%";
												state.elements.popper.style.left = "50%";
												state.elements.popper.style.transform = "translate(-50%, -50%)";
											}
											return state;
										},
									},
									{
										name: "preventOverflow",
										enabled: false,
									},
									{
										name: "flip",
										enabled: false,
									},
								],
							}
						: {
								anchorEl: anchorRef.current,
								placement: "bottom-start",
							},
          actionBar: {
						actions: ['clear', 'cancel', 'today', 'accept'],
					},
				}}
				views={["hours", "minutes"]}
				closeOnSelect
			/>
		</Box>
	);
}
